import { supabase } from "@/integrations/supabase/client";

interface ShareAddressData {
  senderId: string;
  receiverId: string;
  address: string;
  additionalInfo?: string | null;
}

interface AddressShare {
  id: string;
  sender_id: string;
  receiver_id: string;
  address: string;
  additional_info: string | null;
  is_active: boolean;
  shared_at: string;
  expires_at: string;
  created_at: string;
}

export const addressSharingService = {
  // Share address with another user
  async shareAddress(data: ShareAddressData): Promise<AddressShare> {
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 1); // 1 hour from now

    const { data: shareData, error } = await supabase
      .from('address_shares')
      .insert({
        sender_id: data.senderId,
        receiver_id: data.receiverId,
        address: data.address,
        additional_info: data.additionalInfo,
        expires_at: expiresAt.toISOString(),
        is_active: true,
      })
      .select()
      .single();

    if (error) throw error;

    // Insert system message into chat
    await this.insertSystemMessage(data.senderId, data.receiverId);

    return shareData;
  },

  // Insert system message into chat
  async insertSystemMessage(senderId: string, receiverId: string): Promise<void> {
    const { error } = await supabase
      .from('messages')
      .insert({
        sender_id: senderId,
        receiver_id: receiverId,
        content: 'shared their address with you. It will be visible for the next 60 minutes.',
        message_type: 'system',
        is_read: false,
      });

    if (error) {
      console.error('Error inserting system message:', error);
      // Don't throw error here as address sharing should still work
    }
  },

  // Stop sharing address
  async stopSharingAddress(shareId: string): Promise<void> {
    const { error } = await supabase
      .from('address_shares')
      .update({ 
        is_active: false,
        expires_at: new Date().toISOString() // Set to expire immediately
      })
      .eq('id', shareId);

    if (error) throw error;
  },

  // Get active address shares sent by user
  async getSharedAddresses(userId: string): Promise<AddressShare[]> {
    const { data, error } = await supabase
      .from('address_shares')
      .select('*')
      .eq('sender_id', userId)
      .eq('is_active', true)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Get active address shares received by user
  async getReceivedAddresses(userId: string): Promise<AddressShare[]> {
    const { data, error } = await supabase
      .from('address_shares')
      .select('*')
      .eq('receiver_id', userId)
      .eq('is_active', true)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Get specific address share by ID
  async getAddressShare(shareId: string): Promise<AddressShare | null> {
    const { data, error } = await supabase
      .from('address_shares')
      .select('*')
      .eq('id', shareId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  // Check if address is still active
  async checkAddressActive(shareId: string): Promise<boolean> {
    const share = await this.getAddressShare(shareId);
    if (!share) return false;
    
    const now = new Date();
    const expiresAt = new Date(share.expires_at);
    
    return share.is_active && expiresAt > now;
  },

  // Clean up expired address shares (can be called periodically)
  async cleanupExpiredShares(): Promise<void> {
    const { error } = await supabase
      .from('address_shares')
      .update({ is_active: false })
      .eq('is_active', true)
      .lt('expires_at', new Date().toISOString());

    if (error) throw error;
  }
};